import React, { useState, KeyboardEvent } from 'react';

/**
 * Props interface for the ChatInput component
 */
interface ChatInputProps {
  // Callback for sending messages
  onSend: (message: string) => void;
  
  // State management
  disabled?: boolean;
  loading?: boolean;
  
  // Customization options
  placeholder?: string;
  maxRows?: number;
  
  // Input validation
  maxLength?: number;
  minLength?: number;
}

/**
 * ChatInput Component
 * 
 * Provides a text input interface for users to type and send messages.
 * Features:
 * - Multi-line textarea with auto-resize
 * - Enter to send, Shift+Enter for new line
 * - Send button with loading state
 * - Character count and validation
 * - Disabled state during processing
 * - Keyboard shortcuts and accessibility
 * 
 * This component extracts the chat input logic from the main FillyOnIris component
 * to improve maintainability and reusability.
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  loading = false,
  placeholder = "Type your message...",
  maxRows = 4,
  maxLength = 2000,
  minLength = 1
}) => {
  // Local state for input value
  const [inputValue, setInputValue] = useState("");
  
  // Determine if component should be disabled
  const isDisabled = disabled || loading;
  
  // Determine if send button should be enabled
  const canSend = !isDisabled && 
                  inputValue.trim().length >= minLength && 
                  inputValue.length <= maxLength;

  /**
   * Handles sending the message
   * Clears input after sending
   */
  const handleSend = () => {
    if (canSend) {
      onSend(inputValue.trim());
      setInputValue("");
    }
  };

  /**
   * Handles keyboard events in the textarea
   * Enter = send message, Shift+Enter = new line
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Handles input value changes with validation
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    // Enforce max length
    if (value.length <= maxLength) {
      setInputValue(value);
    }
  };

  /**
   * Calculates character count for display
   */
  const characterCount = inputValue.length;
  const isNearLimit = characterCount > maxLength * 0.8;
  const isOverLimit = characterCount > maxLength;

  return (
    <div className="chatbot-input-area">
      <div className="chatbot-input-content">
        {/* Main Input Container */}
        <div className="chatbot-input-container">
          {/* Textarea */}
          <textarea
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isDisabled ? "Please wait..." : placeholder}
            className="chatbot-textarea"
            disabled={isDisabled}
            rows={maxRows}
            maxLength={maxLength}
            aria-label="Message input"
            aria-describedby="input-help character-count"
          />
          
          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="chatbot-send-btn"
            aria-label="Send message"
            title={!canSend ? "Enter a message to send" : "Send message (Enter)"}
          >
            {loading ? (
              // Loading spinner
              <div className="chatbot-send-spinner">
                <div className="chatbot-spinner-dot"></div>
                <div className="chatbot-spinner-dot"></div>
                <div className="chatbot-spinner-dot"></div>
              </div>
            ) : (
              // Send icon
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
                />
              </svg>
            )}
          </button>
        </div>
        
        {/* Input Help and Character Count */}
        <div className="chatbot-input-footer">
          {/* Help Text */}
          <p id="input-help" className="chatbot-input-hint">
            Press Enter to send • Shift + Enter for new line
          </p>
          
          {/* Character Count */}
          <div 
            id="character-count" 
            className={`chatbot-character-count ${isNearLimit ? 'warning' : ''} ${isOverLimit ? 'error' : ''}`}
          >
            <span>{characterCount}</span>
            <span className="chatbot-character-limit">/{maxLength}</span>
          </div>
        </div>
        
        {/* Validation Messages */}
        {isOverLimit && (
          <div className="chatbot-input-error">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
            <span>Message is too long. Please shorten your message.</span>
          </div>
        )}
        
        {inputValue.trim().length > 0 && inputValue.trim().length < minLength && (
          <div className="chatbot-input-warning">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>Please enter a message to continue.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
